import { useState } from 'react'
import PropTypes from 'prop-types'

export const ChatInput = ({
  onSendMessage,
  loading,
  isChatMode,
  visible,
  animated,
  error,
  disabled,
  onFocus,
  isUpdateAvailable,
}) => {
  const [message, setMessage] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!message.trim() || loading) return

    onSendMessage(message)
    setMessage('')
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className={`flex justify-center ${visible || isChatMode ? 'block' : 'invisible'}`}
      >
        <div
          className={`relative transition-all duration-[400ms] ease-out ${
            animated || isChatMode ? 'w-full opacity-100' : 'w-0 opacity-0'
          }`}
        >
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              isUpdateAvailable
                ? 'Update required - please update the app to continue...'
                : disabled
                  ? 'Complete security verification first...'
                  : isChatMode
                    ? 'Type your message...'
                    : 'Ask any neuroscience question...'
            }
            disabled={loading || disabled}
            onFocus={onFocus}
            data-testid="chat-input"
            className="w-full px-6 py-4 pr-12 bg-dark-secondary border border-dark-border rounded-full text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-gray-400 text-lg disabled:opacity-50 transition-all duration-200"
          />
          <button
            type="submit"
            disabled={loading || !message.trim() || isUpdateAvailable}
            className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 focus:ring-offset-gray-900 ${
              isUpdateAvailable
                ? 'text-orange-500'
                : message.trim()
                  ? 'text-gray-300 hover:text-gray-100 hover:bg-dark-tertiary'
                  : 'text-gray-600'
            } rounded-lg disabled:opacity-50 disabled:cursor-not-allowed`}
            aria-label={isUpdateAvailable ? 'Update required' : 'Send message'}
            title={isUpdateAvailable ? 'An update is required to continue' : ''}
          >
            {isUpdateAvailable ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            )}
          </button>
        </div>
      </form>

      {error && (
        <div
          className={`mt-4 p-3 bg-red-900/20 border border-red-500/20 rounded-lg text-red-400 text-sm text-center transition-opacity duration-700 ${
            animated || isChatMode ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {error}
        </div>
      )}
    </>
  )
}

ChatInput.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  isChatMode: PropTypes.bool,
  visible: PropTypes.bool,
  animated: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  onFocus: PropTypes.func,
  isUpdateAvailable: PropTypes.bool,
}

ChatInput.defaultProps = {
  loading: false,
  isChatMode: false,
  visible: true,
  animated: false,
  error: null,
  disabled: false,
  onFocus: null,
  isUpdateAvailable: false,
}
